import { format, isValid, parse } from "date-fns";
import { BUSINESS_TYPE_OPTIONS } from "@/components/TransactionForm";
import { newLine, coaOptionsFor } from "./multiEntry";

const ALIASES = {
  date: ["date", "txn date", "transaction date", "posted", "posting date", "value date"],
  type: ["type", "kind", "account type", "transaction type"],
  category: ["category", "department", "dept", "class"],
  coa_code: ["gl", "gl code", "gl account", "coa", "account code", "code"],
  account_id: ["account", "wallet", "bank", "account name", "card"],
  note: ["memo", "note", "notes", "description", "details", "narration", "payee"],
  amount: ["amount", "value", "total", "sum"],
};
const ORDER_BIZ = ["date", "type", "category", "coa_code", "account_id", "note", "amount"];
const ORDER_PER = ["date", "type", "category", "account_id", "note", "amount"];
const DATE_FORMATS = ["yyyy-MM-dd", "MM/dd/yyyy", "M/d/yyyy", "dd/MM/yyyy", "MM/dd/yy", "M/d/yy",
  "dd-MMM-yyyy", "d MMM yyyy", "MMM d, yyyy", "MMMM d, yyyy", "yyyy/MM/dd", "dd.MM.yyyy"];
const TYPE_WORDS = {
  expense: "expense", expenses: "expense", debit: "expense", dr: "expense", out: "expense", payment: "expense",
  income: "income", revenue: "income", credit: "income", cr: "income", in: "income", deposit: "income",
  saving: "saving", savings: "saving", asset: "saving", assets: "saving", liability: "saving",
  liabilities: "saving", equity: "saving",
};
const KIND_WORDS = { revenue: "revenue", income: "revenue", credit: "revenue", expense: "expense", expenses: "expense",
  debit: "expense", asset: "asset", assets: "asset", liability: "liability", liabilities: "liability",
  equity: "equity", capital: "equity", saving: "asset" };

const norm = (s) => String(s ?? "").trim().toLowerCase().replace(/\s+/g, " ");

export function splitClipboard(text) {
  const rows = String(text || "").replace(/\r\n?/g, "\n").split("\n").filter((r) => r.trim() !== "");
  const delim = rows.some((r) => r.includes("\t")) ? "\t" : ",";
  return rows.map((r) => splitRow(r, delim));
}

function splitRow(row, delim) {
  if (delim === "\t") return row.split("\t").map((c) => c.trim());
  const out = []; let cur = ""; let q = false;
  for (const ch of row) {
    if (ch === '"') q = !q;
    else if (ch === "," && !q) { out.push(cur.trim()); cur = ""; }
    else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

export function detectColumns(firstRow, isBusiness) {
  const map = firstRow.map((h) => {
    const n = norm(h);
    return Object.keys(ALIASES).find((f) => ALIASES[f].includes(n)) || null;
  });
  const hits = map.filter(Boolean).length;
  if (hits >= 2 || (hits === 1 && firstRow.length === 1)) return { hasHeader: true, columns: map };
  const order = isBusiness ? ORDER_BIZ : ORDER_PER;
  return { hasHeader: false, columns: firstRow.map((_, i) => order[i] || null) };
}

export function parseDate(s) {
  const v = String(s || "").trim();
  if (!v) return "";
  for (const f of DATE_FORMATS) {
    const d = parse(v, f, new Date());
    if (isValid(d) && d.getFullYear() > 1900) return format(d, "yyyy-MM-dd");
  }
  const d = new Date(v);
  return isValid(d) ? format(d, "yyyy-MM-dd") : "";
}

export function parseAmount(s) {
  let v = String(s || "").trim();
  if (!v) return null;
  const neg = /^\(.*\)$/.test(v) || /^-/.test(v) || /-$/.test(v);
  v = v.replace(/[^0-9.,]/g, "");
  if (/,\d{2}$/.test(v) && !/\.\d/.test(v)) v = v.replace(/\./g, "").replace(",", ".");
  else v = v.replace(/,/g, "");
  const n = parseFloat(v);
  return Number.isFinite(n) ? (neg ? -n : n) : null;
}

const findBy = (list, raw, keys) => {
  const n = norm(raw);
  if (!n) return null;
  return list.find((x) => keys.some((k) => norm(x[k]) === n))
    || list.find((x) => keys.some((k) => norm(x[k]).startsWith(n) || n.startsWith(norm(x[k]) || "\u0000")));
};

function mapRow(cells, columns, ctx, signedTypes) {
  const raw = {};
  columns.forEach((f, i) => { if (f && raw[f] == null) raw[f] = cells[i] ?? ""; });
  const line = { ...newLine(), unmatched: {} };
  const miss = (f, v) => { if (String(v || "").trim()) line.unmatched[f] = String(v).trim(); };

  line.date = parseDate(raw.date) || (raw.date ? "" : line.date);
  if (raw.date && !line.date) miss("date", raw.date);

  const amt = parseAmount(raw.amount);
  if (amt == null) miss("amount", raw.amount);
  line.amount = amt == null ? "" : String(Math.abs(amt));

  const t = norm(raw.type);
  if (ctx.isBusiness) {
    const kind = KIND_WORDS[t] || (t ? null : (signedTypes && amt > 0 ? "revenue" : "expense"));
    if (!kind) miss("type", raw.type);
    line.business_kind = kind || "expense";
    line.type = BUSINESS_TYPE_OPTIONS.find((o) => o.value === line.business_kind).tx_type;
  } else {
    const ty = TYPE_WORDS[t] || (t ? null : (signedTypes && amt > 0 ? "income" : "expense"));
    if (!ty) miss("type", raw.type);
    line.type = ty || "expense";
  }

  const cat = findBy(ctx.categories[line.type] || [], raw.category, ["name"]);
  line.category = cat?.name || "";
  if (!cat) miss("category", raw.category);

  const acc = findBy(ctx.accounts, raw.account_id, ["name"]);
  line.account_id = acc?.id || (String(raw.account_id || "").trim() ? "" : "none");
  if (!acc) miss("account_id", raw.account_id);

  if (ctx.isBusiness) {
    const code = String(raw.coa_code || "").split(/\s|·|-/)[0];
    const allowed = coaOptionsFor(line, ctx.coa, true);
    const coa = allowed.find((c) => norm(c.code) === norm(code)) || findBy(allowed, raw.coa_code, ["name"]);
    line.coa_code = coa?.code || (String(raw.coa_code || "").trim() ? "" : "none");
    if (!coa) miss("coa_code", raw.coa_code);
  }
  line.note = raw.note || "";
  return line;
}

export function parsePaste(text, ctx) {
  const rows = splitClipboard(text);
  if (!rows.length) return { lines: [], columns: [], hasHeader: false };
  const { hasHeader, columns } = detectColumns(rows[0], ctx.isBusiness);
  const data = hasHeader ? rows.slice(1) : rows;
  const amtIdx = columns.indexOf("amount");
  const hasType = columns.includes("type");
  const signedTypes = !hasType && amtIdx >= 0 && data.some((r) => (parseAmount(r[amtIdx]) || 0) < 0);
  const lines = data.map((r) => mapRow(r, columns, ctx, signedTypes))
    .filter((l) => l.amount || l.note || l.category);
  return { lines, columns, hasHeader };
}

export const FIELD_LABEL = {
  date: "Date", type: "Type", category: "Category", coa_code: "GL code",
  account_id: "Account", note: "Memo", amount: "Amount",
};
